import { Link } from 'react-router';
import clsx from 'clsx';
import { Coins, Image as ImageIcon, Play, Star } from 'lucide-react';
import { templateFileUrl } from '@/api/client';
import type { TemplateCard as T } from '@/api/types';
import { useT } from '@/lib/i18n';

export function Stars({ value, size = 'size-3.5' }: { value: number | null; size?: string }) {
  if (value === null) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[#f2a900]" aria-label={`${value} / 5`}>
      <Star className={`${size} fill-current`} aria-hidden />
      <span className="text-xs font-semibold text-ink-2">{value.toFixed(1)}</span>
    </span>
  );
}

// "kuanzia tokeni 20 · Tsh 2,000" (spec §3), on the media.
export function PriceChip({ tokens, tzs, className }: { tokens: number; tzs: number; className?: string }) {
  const { t } = useT();
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[12px] leading-tight font-bold text-ink shadow-sm', className)}>
      <Coins className="size-3 text-brand" aria-hidden />
      {t('v2.price_from', { n: tokens })}<span className="font-medium text-muted">· {t('v2.tsh', { tzs: tzs.toLocaleString('en-US') })}</span>
    </span>
  );
}

export function TemplateCard({ t: tpl, compact }: { t: T; compact?: boolean }) {
  const { t, lang } = useT();
  const cover = tpl.cover ? templateFileUrl(tpl.id, tpl.cover) : null;
  return (
    <Link to={`/t/${tpl.id}`} className="group block">
      <div className={clsx('relative overflow-hidden rounded-[16px] bg-[#eee] shadow-card transition group-hover:-translate-y-1 group-hover:shadow-lift', compact && tpl.kind === 'video' ? 'aspect-[9/16]' : 'aspect-[4/5]', compact && 'rounded-[12px]')}>
        {cover && <img src={cover} alt="" loading="lazy" className="size-full object-cover transition duration-500 group-hover:scale-105" />}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-0.5 text-[0.7rem] font-semibold text-white">
          {tpl.kind === 'video' ? <Play className="size-3 fill-current" /> : <ImageIcon className="size-3" />}
          {tpl.kind === 'video' ? `${t('tg.kind_video')}${tpl.seconds ? ` ${tpl.seconds}s` : ''}` : t('tg.kind_image')}
        </span>
        {(tpl.trending ?? tpl.featured) && <span className="absolute top-2 right-2 rounded-full bg-brand px-2 py-0.5 text-[0.7rem] font-semibold text-white">{t('tg.trending')}</span>}
        {tpl.price_from_tokens != null && <PriceChip tokens={tpl.price_from_tokens} tzs={tpl.price_from_tzs ?? 0} className="absolute bottom-2 left-2" />}
      </div>
      <div className={clsx('flex items-start justify-between gap-2', compact ? 'mt-1.5' : 'mt-2')}>
        <b className={clsx('leading-tight', compact ? 'text-[13px] font-semibold' : 'text-[0.95rem]')}>{tpl.title[lang] || tpl.title.sw}</b>
        <Stars value={tpl.rating} />
      </div>
      <p className="text-xs text-muted">{tpl.designer?.name ? t('tg.by', { name: tpl.designer.name }) : tpl.designer ? '' : t('tg.team')}</p>
    </Link>
  );
}
